import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Select } from 'antd';
import { FileTextOutlined, SettingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import WorkflowSimpleMappingConfigureDrawer from './WorkflowSimpleMappingConfigureDrawer';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import { updateData } from './workflowHelpers';
import { getInvoiceStatusLabels } from '../../payables/invoices/state/invoices.actions';

const invoiceFields = [
  { key: 'invoiceNumber', text: 'Invoice Number', type: 'text' },
  { key: 'amount', text: 'Invoice Amount', type: 'text' },
  { key: 'dateIssued', text: 'Date Issued', type: 'dateTime' },
  { key: 'dueDate', text: 'Date Due', type: 'dateTime' },
  { key: 'formId', text: 'Subcontract', type: 'dropdown - PayableSubContracts' },
  { key: 'description', text: 'Description', type: 'text' },
  { key: 'notes', text: 'Notes', type: 'text' },
  { key: 'file', text: 'File', type: 'attachment' },
];

const requiredFields = new Set(['invoiceNumber', 'dateIssued']);

const HOVER_TEXT = 'This step logs an invoice based on the fields submitted.';
const DISABLED_TEXT = 'Add at least a Text Field and a Date Time Field to enable this step';
const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires a <b>Status</b> to be selected,
      {' '}and the <b>Invoice Number</b> and <b>Date Issued</b>
      {' '}field mappings to be configured (via <b>Configure</b>).
    </div>
  ),
};

export default function WorkflowCreatePayableInvoiceNode({
  isDisplay,
  setElements,
  setDataMap,
  name,
  sections = [],
  setDisableNext,
} = {}) {
  function Inner({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const dispatch = useDispatch();

    const {
      fieldMappings: initialFieldMappings = {},
      statusId: initialStatusId,
    } = data;

    const title = `Create Payable Invoice${disabled ? ' - DISABLED' : ''}`;

    const statuses = useSelector((state) => state.invoices.statuses);

    const [showDrawer, setShowDrawer] = useState(false);
    const [fieldMappings, setFieldMappings] = useState(initialFieldMappings);
    const [selectedStatusId, setSelectedStatusId] = useState(initialStatusId);

    const openDrawer = useCallback(() => setShowDrawer(true), []);
    const closeDrawer = useCallback(() => setShowDrawer(false), []);

    const statusOptions = useMemo(() => statuses
      .map(({ id: statusId, title: statusTitle }) => ({
        value: statusId,
        label: statusTitle,
      })), [statuses]);

    useEffect(() => {
      if (statuses?.length) return;
      dispatch(getInvoiceStatusLabels());
    }, [statuses]);

    const disableNext = useMemo(() => !(
      fieldMappings?.invoiceNumber
      && fieldMappings?.dateIssued
      && selectedStatusId
    ), [fieldMappings, selectedStatusId]);

    useEffect(() => {
      if (!setDisableNext) return;
      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [disableNext, setDisableNext, id]);

    const onUpdateData = useCallback((key, setterFunc) => (newValue) => {
      if (!setDataMap || !id) return;
      setterFunc(newValue);
      setDataMap(updateData(id, { [key]: newValue }));
    }, [setDataMap, id]);

    return (
      <WorkflowActionNode
        id={id}
        title={title}
        type="createPayableInvoice"
        Icon={FileTextOutlined}
        draggable={draggable}
        isDisplay={isDisplay || disabled}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
        errorPopover={disableNext ? ERROR_DETAILS : null}
      >
        {!draggable && (
          <div>
            <Row style={{ marginTop: 10 }}>
              Select Status:
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onUpdateData('statusId', setSelectedStatusId)}
                value={selectedStatusId}
                options={statusOptions}
                showSearch
                optionFilterProp="label"
                placeholder="Select a status"
                allowClear
              />
            </Row>
            <Row style={{ margin: '20px 0px' }}>
              <BorderlessButton
                title="Configure"
                style={{ margin: 5 }}
                iconNode={<SettingOutlined />}
                onClick={openDrawer}
              />
            </Row>
            <WorkflowSimpleMappingConfigureDrawer
              id={id}
              visible={showDrawer}
              onClose={closeDrawer}
              onSubmit={closeDrawer}
              sourceName={name}
              sourceSections={sections}
              fieldMappings={fieldMappings}
              onFieldMappingsChange={onUpdateData('fieldMappings', setFieldMappings)}
              data={data}
              setDataMap={setDataMap}
              fields={invoiceFields}
              requiredFields={requiredFields}
              targetHeader="Mapped Field"
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
    );
  }

  Inner.propTypes = {
    id: PropTypes.string.isRequired,
    draggable: PropTypes.bool.isRequired,
    data: PropTypes.shape({
      fieldMappings: PropTypes.object,
      statusId: PropTypes.string,
    }),
    disabled: PropTypes.bool,
  };

  return Inner;
}

WorkflowCreatePayableInvoiceNode.propTypes = {
  isDisplay: PropTypes.bool.isRequired,
  setElements: PropTypes.func.isRequired,
  setDataMap: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  sections: PropTypes.array.isRequired,
  setDisableNext: PropTypes.func.isRequired,
};

WorkflowCreatePayableInvoiceNode.defaultProps = {
  sections: [],
};
